import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";

const inputCls = "h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]";
const TAB = "rounded-lg px-4 py-2 text-sm font-medium text-neutral-400 data-[state=active]:bg-[#E30613] data-[state=active]:text-white transition-colors";
const CARD = "max-w-2xl rounded-2xl border border-white/[0.06] bg-[#141414] p-6 md:p-8";

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div className="space-y-2">
      <Label className="text-neutral-300">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputCls} data-testid={testid} />
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className="flex w-full items-center justify-between rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-4 py-3" data-testid={testid}>
      <span className="text-sm text-neutral-200">{label}</span>
      <span className={`relative h-5 w-9 rounded-full transition-colors ${checked ? "bg-[#E30613]" : "bg-white/10"}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
    </button>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const url = isAdmin ? "/admin/settings" : "/settings";
    api.get(url).then(({ data }) => setS(data)).catch(() => {});
  }, [isAdmin]);

  const sect = (section, key) => (val) => setS((p) => ({ ...p, [section]: { ...(p[section] || {}), [key]: val } }));
  const top = (key) => (val) => setS((p) => ({ ...p, [key]: val }));

  const saveSection = async (section) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", { [section]: s[section] });
      setS(data);
      toast.success("Settings saved");
    } catch (e) { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      // legacy top-level branding fields + branding section
      await api.put("/settings", { app_name: s.app_name, logo: s.logo, primary_color: s.primary_color, secondary_color: s.secondary_color });
      const { data } = await api.put("/admin/settings", { branding: s.branding });
      setS(data);
      toast.success("Branding saved");
    } catch (e) { toast.error("Failed to save branding"); }
    finally { setSaving(false); }
  };

  const SaveBtn = ({ onClick }) => (
    <button type="button" onClick={onClick} disabled={saving} className="mt-8 flex h-11 items-center justify-center rounded-lg bg-[#E30613] px-6 font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="settings-save">
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
    </button>
  );

  if (!s) return <DashboardLayout title="Settings"><div className="flex items-center justify-center py-20 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div></DashboardLayout>;

  if (!isAdmin) {
    return (
      <DashboardLayout title="Settings">
        <div className={CARD}>
          <p className="text-sm text-neutral-500">Platform configuration is managed by SGBINOXE administrators.</p>
        </div>
      </DashboardLayout>
    );
  }

  const g = s.general || {}, b = s.branding || {}, e = s.email_templates || {}, sec = s.security || {}, app = s.application || {};

  return (
    <DashboardLayout title="Settings">
      <div className="mb-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Configuration</p>
        <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">System settings</h2>
        <p className="mt-1 text-sm text-neutral-500">Manage general info, branding, email, security and application preferences.</p>
      </div>

      <Tabs defaultValue="general">
        <TabsList className="mb-6 flex h-auto flex-wrap justify-start gap-2 bg-transparent p-0" data-testid="settings-tabs">
          <TabsTrigger value="general" className={TAB} data-testid="tab-general">General</TabsTrigger>
          <TabsTrigger value="branding" className={TAB} data-testid="tab-branding">Branding</TabsTrigger>
          <TabsTrigger value="email" className={TAB} data-testid="tab-email">Email</TabsTrigger>
          <TabsTrigger value="security" className={TAB} data-testid="tab-security">Security</TabsTrigger>
          <TabsTrigger value="application" className={TAB} data-testid="tab-application">Application</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <div className={CARD}>
            <div className="space-y-5">
              <Field label="Company Name" value={g.company_name} onChange={sect("general", "company_name")} testid="set-company-name" />
              <Field label="Support Email" value={g.support_email} onChange={sect("general", "support_email")} testid="set-support-email" />
              <Field label="Support Phone" value={g.support_phone} onChange={sect("general", "support_phone")} testid="set-support-phone" />
              <Field label="Website" value={g.website} onChange={sect("general", "website")} placeholder="https://…" testid="set-website" />
              <Field label="Business Address" value={g.business_address} onChange={sect("general", "business_address")} testid="set-address" />
              <Field label="Timezone" value={g.timezone} onChange={sect("general", "timezone")} placeholder="UTC" testid="set-timezone" />
            </div>
            <SaveBtn onClick={() => saveSection("general")} />
          </div>
        </TabsContent>

        <TabsContent value="branding">
          <div className={CARD}>
            <div className="space-y-5">
              <Field label="Application Name" value={s.app_name} onChange={top("app_name")} testid="settings-app-name" />
              <Field label="Logo URL" value={s.logo} onChange={top("logo")} placeholder="https://…" testid="settings-logo" />
              <Field label="Favicon URL" value={b.favicon} onChange={sect("branding", "favicon")} placeholder="https://…" testid="set-favicon" />
              <Field label="Email Footer Logo URL" value={b.email_footer_logo} onChange={sect("branding", "email_footer_logo")} placeholder="https://…" testid="set-email-logo" />
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-neutral-300">Primary Color</Label>
                  <div className="flex items-center gap-3"><span className="h-11 w-11 rounded-lg border border-white/10" style={{ background: s.primary_color }} /><Input value={s.primary_color || ""} onChange={(ev) => top("primary_color")(ev.target.value)} className={inputCls} data-testid="settings-primary" /></div>
                </div>
                <div className="space-y-2">
                  <Label className="text-neutral-300">Secondary Color</Label>
                  <div className="flex items-center gap-3"><span className="h-11 w-11 rounded-lg border border-white/10" style={{ background: s.secondary_color }} /><Input value={s.secondary_color || ""} onChange={(ev) => top("secondary_color")(ev.target.value)} className={inputCls} data-testid="settings-secondary" /></div>
                </div>
              </div>
            </div>
            <SaveBtn onClick={saveBranding} />
          </div>
        </TabsContent>

        <TabsContent value="email">
          <div className={CARD}>
            <div className="space-y-5">
              <div className="space-y-2"><Label className="text-neutral-300">Certificate Email</Label><Textarea rows={3} value={e.certificate ?? ""} onChange={(ev) => sect("email_templates", "certificate")(ev.target.value)} className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="set-email-certificate" /></div>
              <div className="space-y-2"><Label className="text-neutral-300">Follow-Up Reminder</Label><Textarea rows={3} value={e.follow_up ?? ""} onChange={(ev) => sect("email_templates", "follow_up")(ev.target.value)} className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="set-email-followup" /></div>
              <div className="space-y-2"><Label className="text-neutral-300">Installer Welcome</Label><Textarea rows={3} value={e.welcome ?? ""} onChange={(ev) => sect("email_templates", "welcome")(ev.target.value)} className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="set-email-welcome" /></div>
              <div className="space-y-2"><Label className="text-neutral-300">Account Approval</Label><Textarea rows={3} value={e.approval ?? ""} onChange={(ev) => sect("email_templates", "approval")(ev.target.value)} className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="set-email-approval" /></div>
            </div>
            <SaveBtn onClick={() => saveSection("email_templates")} />
          </div>
        </TabsContent>

        <TabsContent value="security">
          <div className={CARD}>
            <div className="space-y-5">
              <Field label="Minimum Password Length" type="number" value={sec.password_min_length} onChange={(v) => sect("security", "password_min_length")(parseInt(v, 10) || 0)} testid="set-pw-length" />
              <div className="space-y-3">
                <Toggle label="Require uppercase letter" checked={!!sec.password_require_uppercase} onChange={sect("security", "password_require_uppercase")} testid="set-pw-upper" />
                <Toggle label="Require lowercase letter" checked={!!sec.password_require_lowercase} onChange={sect("security", "password_require_lowercase")} testid="set-pw-lower" />
                <Toggle label="Require number" checked={!!sec.password_require_number} onChange={sect("security", "password_require_number")} testid="set-pw-number" />
                <Toggle label="Require special character" checked={!!sec.password_require_special} onChange={sect("security", "password_require_special")} testid="set-pw-special" />
              </div>
              <Field label="Session Timeout (minutes)" type="number" value={sec.session_timeout_minutes} onChange={(v) => sect("security", "session_timeout_minutes")(parseInt(v, 10) || 0)} testid="set-session-timeout" />
              <Field label="Maximum Login Attempts" type="number" value={sec.max_login_attempts} onChange={(v) => sect("security", "max_login_attempts")(parseInt(v, 10) || 0)} testid="set-max-attempts" />
              <Field label="Lockout Duration (minutes)" type="number" value={sec.lockout_minutes} onChange={(v) => sect("security", "lockout_minutes")(parseInt(v, 10) || 0)} testid="set-lockout" />
            </div>
            <SaveBtn onClick={() => saveSection("security")} />
          </div>
        </TabsContent>

        <TabsContent value="application">
          <div className={CARD}>
            <div className="space-y-5">
              <Field label="Follow-Up Window (days)" type="number" value={app.follow_up_days} onChange={(v) => sect("application", "follow_up_days")(parseInt(v, 10) || 0)} placeholder="30" testid="set-followup-days" />
              <Field label="Default Credits Per Bottle" type="number" value={app.credits_per_bottle} onChange={(v) => sect("application", "credits_per_bottle")(parseInt(v, 10) || 0)} placeholder="10" testid="set-credits-per-bottle" />
              <Field label="Reminder Send Hour (UTC)" type="number" value={app.reminder_hour} onChange={(v) => sect("application", "reminder_hour")(parseInt(v, 10) || 0)} placeholder="9" testid="set-reminder-hour" />
            </div>
            <SaveBtn onClick={() => saveSection("application")} />
          </div>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
